import { useToast } from "@smakecloud/smake-ui";
import { type AxiosError, isAxiosError } from "axios";

export type ErrorResponse = {
    errors?: Record<string, string[]>;
    message: string;
};

type LaravelError = AxiosError & {
    response: {
        data: LaravelErrorResponse;
    };
    status: number;
};

type LaravelErrorResponse = {
    message: string;
};

export class ValidationError extends Error {
    errors: ErrorResponse["errors"];
    constructor(message: string, errors: ErrorResponse["errors"]) {
        super(message);
        this.errors = errors;
    }
}

// eslint-disable-next-line no-restricted-exports, unicorn/no-anonymous-default-export
export default () => {
    const { t } = useI18n();
    const { slug } = storeToRefs(useSlugStore());
    const { showErrorToast } = useToast();

    /**
     * Handle Errors in the Frontend.
     * @param error The thrown error
     * @param model The model name from where the possible error is coming from
     */
    async function errorHandler(error: unknown, model?: string) {
        if (isAxiosError<ErrorResponse>(error)) {
            // unauthorized
            if (error.response?.status === 401 && slug.value) {
                await useTokenStore().navigateToLogin();
            }
            // not found
            else
                switch (error.response?.status) {
                    case 404:
                        if (error.config?.method === "get") {
                            showError({
                                message: error.response.data.message,
                                statusCode: error.response.status,
                                statusMessage: t("helpers.flash.not_found", {
                                    model,
                                }),
                            });
                        } else {
                            showErrorToast(t("helpers.flash.not_found"));
                        }
                        // Form errors

                        break;

                    case 422:
                        showErrorToast(error.response.data.message);

                        throw new ValidationError(
                            error.response.data.message,
                            error.response.data.errors,
                        );

                    case 500:
                        showErrorToast(t("helpers.errors.500.message"));

                        throw new Error(error.response.data.message);

                    default:
                        showErrorToast(t("helpers.errors.500.message"));
                }
        }
    }

    /**
     * Wraps a Promise (usually an axios request) with the error Handler
     * @param promise The Promise you want to wrap with the error Handler
     * @param model The model name from where the possible error is coming from
     */
    async function handlePromise<T>(
        promise: Promise<T>,
        model?: string,
    ): Promise<T> {
        try {
            const t = await promise;

            return t;
        } catch (error) {
            await errorHandler(error, model);
            throw error;
        }
    }

    // eslint-disable-next-line unicorn/consistent-function-scoping
    function isLaravelError(element: unknown): element is LaravelError {
        if (!isAxiosError<LaravelErrorResponse>(element)) {
            return false;
        }

        if (!element.response) {
            return false;
        }

        if (
            !("message" in element.response.data) ||
            typeof element.response.data.message !== "string"
        ) {
            return false;
        }

        if (element.status === undefined) return false;

        return !(
            /The (POST|GET|PUT|DELETE|PATCH) method is not supported/.exec(
                element.response.data.message,
            ) ??
            /Logo Type 'image' is not supported by Merchant Frontend API/.exec(
                element.response.data.message,
            )
        );
    }

    return {
        errorHandler,
        handlePromise,
        isLaravelError,
    };
};
